/**
 * A single editable parameter.
 *
 * Built entirely from the engine's parameter registry, so adding a field to the model
 * makes it editable here with no change to this file. The control shows three things
 * beyond the value itself, and all three exist for the same reason — that this model is
 * meant to be adapted rather than merely watched:
 *
 *   - what the parameter means, in plain language
 *   - whether it is something anyone has measured, or something we guessed
 *   - what data would pin it down, if you wanted to replace our number with yours
 *
 * The evidence badge is the important one. Most of these are `judgement` (29 of 46), and a
 * reader deciding how much to trust a run deserves to see that at the point of use rather
 * than in a footnote.
 */

import { useId, useState } from 'react';
import type { ParameterMeta } from 'walkout-engine';
import './ParameterControl.css';

const EVIDENCE_LABEL: Record<ParameterMeta['evidence'], string> = {
  measurable: 'measured',
  estimable: 'estimated',
  judgement: 'guessed',
};

const EVIDENCE_TITLE: Record<ParameterMeta['evidence'], string> = {
  measurable: 'Official statistics exist for this. If ours disagree with yours, yours are probably right.',
  estimable: 'No direct measurement, but survey or case evidence constrains it.',
  judgement: 'Nobody has measured this and nobody can. Treat any specific value as a question.',
};

function format(value: number, meta: ParameterMeta): string {
  if (meta.unit === 'days') return `${Math.round(value)}d`;
  if (meta.unit === 'share') {
    const percent = value * 100;
    return percent >= 10 || percent === 0 ? `${Math.round(percent)}%` : `${percent.toFixed(1)}%`;
  }
  return value.toFixed(meta.step < 0.01 ? 3 : 2);
}

export interface ParameterControlProps {
  meta: ParameterMeta;
  value: number;
  /** The scenario's own value, so an edit can be seen and undone. */
  baseline: number;
  onChange: (value: number) => void;
}

export function ParameterControl({ meta, value, baseline, onChange }: ParameterControlProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const edited = Math.abs(value - baseline) > meta.step / 1000;

  return (
    <div className={`param${edited ? ' param--edited' : ''}`}>
      <div className="param__head">
        <label className="param__label" htmlFor={id}>
          {meta.label}
        </label>
        <button
          type="button"
          className={`param__evidence param__evidence--${meta.evidence}`}
          title={`${EVIDENCE_TITLE[meta.evidence]}\n\n${meta.sourcedBy}`}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {EVIDENCE_LABEL[meta.evidence]}
        </button>
        <output className="param__value numeral" htmlFor={id}>
          {format(value, meta)}
        </output>
      </div>

      <input
        id={id}
        className="param__slider"
        type="range"
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />

      <div className="param__foot">
        <p className="param__description">{meta.description}</p>
        {edited && (
          <button type="button" className="param__reset" onClick={() => onChange(baseline)}>
            reset to {format(baseline, meta)}
          </button>
        )}
      </div>

      {open && (
        <div className="param__sourcing">
          <p className="param__sourcing-evidence">{EVIDENCE_TITLE[meta.evidence]}</p>
          <p className="param__sourcing-how">
            <span className="label">Where a real figure would come from</span>
            {meta.sourcedBy}
          </p>
        </div>
      )}
    </div>
  );
}
